<template>
    <div class="contact">
        <div class="top-white-box"></div>
        <div class="top-gray-box">
            <h1>Contact Us</h1>
        </div>
        <div class="page-wrapper">
            <div class="page-contents">

                <div class="contact-us-info-container" v-if="settings.contactEmail || settings.contactPhone">
                    <div class="info-wrapper">
                        <div class="info-container" v-if="settings.contactEmail">
                            <div class="info-label">Email</div>
                            <div class="info-data">{{settings.contactEmail}}</div>
                        </div>
                        <div class="info-container" v-if="settings.contactPhone">
                            <div class="info-label">Phone</div>
                            <div class="info-data">{{settings.contactPhone}}</div>
                        </div>
                    </div>
                </div>

                <div v-if="settings.contactPhone" class="contact-us-whatsapp">
                    <div>
                        Have a question or comment? Send us a message.<br /><br />
                    </div>
                    <a class="button" :href="whatsapp_msg_url" target="_blank">Contact Us Via WhatsApp</a>
                </div>
                <div v-else class="form-container">
                    <div>
                        Have a question or comment? Send us a message. Fill out the form below and click submit.
                    </div>
                    <div id="contact-us-form">
                        <div>
                            <label>Name</label>
                            <input placeholder="Your Name" id="name" name="name" v-model="form.name">
                        </div>
                        <div>
                            <label>Email</label>
                            <input placeholder="Your Email" id="email" name="email" v-model="form.email">
                        </div>
                        <div>
                            <label>Phone (optional)</label>
                            <input placeholder="Your Phone" id="phone" name="phone" v-model="form.phone">
                        </div>
                        <div>
                            <label>Message</label>
                            <textarea cols="400"
                                      rows="4"
                                      id="message"
                                      name="message"
                                      placeholder="Your Message"
                                      v-model="form.message"></textarea>
                        </div>
                        <div class="submit-button-container">
                            <h-captcha
                                :sitekey="settings.hcaptchaSiteKey"
                                @verify="onVerify"
                                @error="onError"
                            />
                            <button v-show="isBusy" class="button">
                                <span class="icon"><img :src="`/Assets/img/spin.svg`" /></span>
                            </button>
                            <span class="thank-you-message" v-if="showThankYouMessage">Thank you. We will be in touch soon.</span>
                        </div>
                    </div>
                    <div>
                        This site is protected by hCaptcha and its <a href="https://www.hcaptcha.com/privacy" target="_blank">Privacy Policy</a> and <a href="https://www.hcaptcha.com/terms" target="_blank">Terms of Service</a> apply.
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue'
import api from "@/api"
import settings from '@/settings'
import hCaptcha from '@hcaptcha/vue3-hcaptcha'

interface ContactForm {
    name: string
    phone: string
    email: string
    message: string
    token: string | null
    whatsapp_msg_url: string | null
}

export default defineComponent({
    components: {
        hCaptcha
    },

    setup() {
        const isBusy = ref(false)
        const showThankYouMessage = ref(false)
        const form = ref<ContactForm>({
            name: '',
            phone: '',
            email: '',
            message: '',
            token: null,
            whatsapp_msg_url: null
        })

        const isValid = computed(() => {
            return form.value.name !== '' && form.value.email !== '' && form.value.message !== ''
        })

        const whatsapp_msg_url = computed(() => {
            return settings.contactPhone ? 
                `https://wa.me/1${settings.contactPhone.replaceAll('.', '')}?text=Contact Us` : 
                null
        })

        const onVerify = async (token: string) => {
            if (!isValid.value) return

            isBusy.value = true
            showThankYouMessage.value = false

            try {
                form.value.token = token
                await api.submitContactUsForm(form.value)
                isBusy.value = false
                showThankYouMessage.value = true
            } catch (error) {
                console.error('Error submitting form:', error)
                isBusy.value = false
            }
        }

        const onError = (error: Error) => {
            console.error('hCaptcha Error:', error)
            isBusy.value = false
        }

        onMounted(() => {
            document.title = settings.siteNameCaps + ' | Contact'
        })

        return {
            isBusy,
            showThankYouMessage,
            form,
            isValid,
            settings,
            whatsapp_msg_url,
            onVerify,
            onError
        }
    }
})
</script>
